/**
 * NCS : comportements communs des sections Nails Concept Shop (Horizon 4.1.3).
 *
 * - NCS.addLines(lines, source) : ajout groupe au panier par l'API Ajax, avec l'evenement standard
 *   CartLinesUpdateEvent attendu par Horizon (tiroir, pastille du panier et lignes se mettent a jour seuls).
 * - Steppers de quantite [data-ncs-stepper] : boutons [data-ncs-step="-1|1"] et champ [data-ncs-qty].
 * - Date d'expedition [data-ncs-shipby] : jours ouvres depuis Lyon, jamais une date fausse.
 * - Liens WhatsApp [data-ncs-wa] : message prerempli.
 * Aucun ecouteur scroll, delegation d'evenements, idempotent.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const LOCALE = document.documentElement.lang || 'fr';
const CURRENCY = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';

/** Prix au format de la boutique (meme rendu que le filtre money de Liquid). @param {number} cents */
function money(cents) {
  try {
    if (window.NCS_MONEY_FORMAT) return formatMoney(Math.round(cents), window.NCS_MONEY_FORMAT, CURRENCY);
  } catch (e) {
    /* repli ci-dessous */
  }
  try {
    return new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY }).format(cents / 100);
  } catch (e) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }
}

/* Horizon declare `const Theme = {...}` dans un script classique : c'est une liaison globale, pas window.Theme */
function routes() {
  // eslint-disable-next-line no-undef
  const t = typeof Theme !== 'undefined' ? Theme : window.Theme;
  const r = (t && t.routes) || {};
  return { cartAdd: r.cart_add_url || '/cart/add.js', cart: r.cart_url || '/cart' };
}

function cartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return [...new Set(ids)];
}

async function fetchCart() {
  const res = await fetch(`${routes().cart}.js`, { headers: { Accept: 'application/json' } });
  return res.json();
}

/**
 * Ajoute plusieurs lignes au panier en une requete.
 * @param {Array<{id: number|string, quantity: number}>} lines
 * @param {Element} [source] element d'origine (l'evenement remonte depuis lui)
 * @returns {Promise<{ok: boolean, message?: string, count: number}>}
 */
async function addLines(lines, source) {
  const items = lines
    .map((l) => ({ id: Number(l.id), quantity: Math.max(0, Math.floor(Number(l.quantity) || 0)) }))
    .filter((l) => l.id && l.quantity > 0);
  const count = items.reduce((s, l) => s + l.quantity, 0);
  if (!items.length) return { ok: false, message: 'Aucun produit sélectionné.', count: 0 };

  const target = source instanceof Element ? source : document.body;
  const deferred = CartLinesUpdateEvent.createPromise();
  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(routes().cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: cartSectionIds().join(',') }),
    });
    const json = await res.json();
    const cart = await fetchCart();
    if (json.status) {
      target.dispatchEvent(
        new CartErrorEvent({ error: json.message || 'Ajout impossible', code: 'INVALID', detail: { description: json.description } })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'ncs', itemCount: count },
      });
      return { ok: false, message: json.description || json.message || 'Ajout impossible.', count };
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: false, items: cart.items, source: 'ncs', itemCount: count, sections: json.sections },
    });
    announce(count > 1 ? `${count} articles ajoutés au panier` : 'Article ajouté au panier');
    return { ok: true, count };
  } catch (error) {
    deferred.reject(error);
    return { ok: false, message: 'Connexion interrompue, réessayez.', count };
  }
}

/* Message lu par les lecteurs d'ecran */
let liveRegion;
function announce(text) {
  if (!liveRegion) {
    liveRegion = document.createElement('p');
    liveRegion.className = 'ncs-live';
    liveRegion.setAttribute('aria-live', 'polite');
    document.body.append(liveRegion);
  }
  liveRegion.textContent = '';
  requestAnimationFrame(() => {
    liveRegion.textContent = text;
  });
}

/* Bouton d'ajout simple [data-ncs-add-variant="<id de variante>"] (complements du tiroir, cartes NCS) */
document.addEventListener('click', async (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-ncs-add-variant]') : null;
  if (!btn || btn.classList.contains('is-busy')) return;
  event.preventDefault();
  btn.classList.add('is-busy');
  btn.setAttribute('aria-disabled', 'true');
  const result = await addLines([{ id: btn.dataset.ncsAddVariant, quantity: Number(btn.dataset.ncsQty || 1) }], btn);
  btn.classList.remove('is-busy');
  btn.removeAttribute('aria-disabled');
  if (result.ok) {
    btn.classList.add('is-done');
    setTimeout(() => btn.classList.remove('is-done'), 1200);
  } else if (result.message) {
    announce(result.message);
  }
});

/* Steppers de quantite */
function clampQty(input, value) {
  const min = Number(input.min || 0);
  const max = Number(input.max || 99);
  return Math.min(max, Math.max(min, Math.floor(Number(value) || 0)));
}
function syncStepper(stepper) {
  const input = stepper.querySelector('[data-ncs-qty]');
  if (!input) return;
  const v = Number(input.value || 0);
  const minus = stepper.querySelector('[data-ncs-step="-1"]');
  const plus = stepper.querySelector('[data-ncs-step="1"]');
  if (minus) minus.disabled = input.disabled || v <= Number(input.min || 0);
  if (plus) plus.disabled = input.disabled || v >= Number(input.max || 99);
}
document.addEventListener('click', (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-ncs-step]') : null;
  if (!btn) return;
  const stepper = btn.closest('[data-ncs-stepper]');
  const input = stepper && stepper.querySelector('[data-ncs-qty]');
  if (!input || input.disabled) return;
  input.value = String(clampQty(input, Number(input.value || 0) + Number(btn.dataset.ncsStep)));
  syncStepper(stepper);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
});
document.addEventListener('change', (event) => {
  const input = event.target instanceof HTMLInputElement && event.target.matches('[data-ncs-qty]') ? event.target : null;
  if (!input) return;
  const v = clampQty(input, input.value);
  if (String(v) !== input.value) input.value = String(v);
  const stepper = input.closest('[data-ncs-stepper]');
  if (stepper) syncStepper(stepper);
});
function initSteppers(root = document) {
  root.querySelectorAll('[data-ncs-stepper]').forEach(syncStepper);
}

/* Date d'expedition : jours ouvres (lundi a vendredi, hors feries), heure de Paris.
   Sans heure limite connue, on annonce "d'ici le" + N jours ouvres, la promesse de la politique d'expedition. */
const HOLIDAYS = [
  '2026-11-01', '2026-11-11', '2026-12-25', '2027-01-01', '2027-03-29', '2027-05-01', '2027-05-06',
  '2027-05-08', '2027-05-17', '2027-07-14', '2027-08-15', '2027-11-01', '2027-11-11', '2027-12-25',
];
function parisNow() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t).value;
  return { date: new Date(`${get('year')}-${get('month')}-${get('day')}T12:00:00`), hour: Number(get('hour')) };
}
function isBusinessDay(d) {
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return d.getDay() !== 0 && d.getDay() !== 6 && !HOLIDAYS.includes(iso);
}
function shipBy(days = 2, cutoffHour = null) {
  const { date, hour } = parisNow();
  const d = new Date(date);
  let left = days;
  if (cutoffHour !== null && isBusinessDay(d) && hour < cutoffHour) left -= 1;
  while (left > 0) {
    d.setDate(d.getDate() + 1);
    if (isBusinessDay(d)) left -= 1;
  }
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}
function initShipBy(root = document) {
  root.querySelectorAll('[data-ncs-shipby]').forEach((el) => {
    const days = Number(el.dataset.ncsShipby || 2);
    const cutoff = el.dataset.ncsCutoff ? Number(el.dataset.ncsCutoff) : null;
    el.textContent = shipBy(days, cutoff);
  });
}

/* WhatsApp prerempli */
function initWhatsApp(root = document) {
  root.querySelectorAll('a[data-ncs-wa]').forEach((a) => {
    const num = (a.dataset.ncsWa || '').replace(/\D/g, '');
    if (!num) return;
    const text = a.dataset.ncsWaText || '';
    a.href = `https://wa.me/${num}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
  });
}

function init(root = document) {
  initSteppers(root);
  initShipBy(root);
  initWhatsApp(root);
}

window.NCS = Object.assign(window.NCS || {}, { addLines, money, shipBy, announce, init, initSteppers });
init();
document.dispatchEvent(new CustomEvent('ncs:ready'));
document.addEventListener('shopify:section:load', (e) => init(e.target));
